import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CloseIcon from "@mui/icons-material/Close";
import PackageDetailSection from "./PackageDetailSection";

const AiInsightsAndPlanSection = ({ productType, usagePct }) => {
  const loginData = JSON.parse(localStorage.getItem("loginDetails"));
  const [comingSoonOpen, setComingSoonOpen] = useState(false);

  const planName =
    loginData?.subscription
      ?.split("+")[0]
      ?.trim()
      ?.replace(/advanced|dynamic|compact/gi, (m) =>
        ({ advanced: "Total", dynamic: "Personal", compact: "Compact" }[m.toLowerCase()] || m)
      ) || "your";

  /* ── Insight card data ── */
  const insightCards = [
    {
      id: "perf",
      icon: <TrendingUpIcon sx={{ fontSize: 20, color: "#3567D6" }} />,
      iconBg: "#EEF2FF",
      title: `${planName} Plan Performance`,
      body: `Your ${planName} plan usage is at ${usagePct ?? 0}%. You're ${
        usagePct >= 80
          ? "nearing your limit — consider upgrading soon."
          : usagePct >= 50
          ? "on a strong growth trajectory this cycle."
          : "well within your plan capacity for this period."
      }`,
      borderColor: "#BFDBFE",
      bg: "linear-gradient(135deg, #F0F6FF 0%, #EBF0FF 100%)",
    },
    {
      id: "smart",
      icon: <AutoAwesomeIcon sx={{ fontSize: 20, color: "#06B6D4" }} />,
      iconBg: "#ECFEFF",
      title: "Smart Reports Improvement",
      body:
        productType === "vizapp"
          ? "Viz App engagement is improving week over week. AI processing accuracy continues to rise."
          : productType === "videoreport"
          ? "Smart Video Reports are gaining adoption. AI rendering quality improves with each cycle."
          : "Smart Reports performed well above baseline this cycle. AI processing accuracy continues to improve.",
      borderColor: "#A5F3FC",
      bg: "linear-gradient(135deg, #F0FFFE 0%, #E8F7FD 100%)",
    },
  ];

  /* ── Coming Soon Dialog ── */
  const comingSoonDialog = () => (
    <Dialog
      open={comingSoonOpen}
      onClose={() => setComingSoonOpen(false)}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: "20px",
          p: 0.5,
          overflow: "hidden",
          boxShadow: "0 24px 60px rgba(53,103,214,0.22)",
        },
      }}
    >
      <DialogContent sx={{ pt: 4, pb: 2.5, px: 3.5, position: "relative" }}>
        {/* Close X */}
        <IconButton
          onClick={() => setComingSoonOpen(false)}
          sx={{ position: "absolute", right: 10, top: 10, color: "#9DA3AE" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        {/* Icon */}
        <Stack alignItems="center" mb={2}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "18px",
              background: "linear-gradient(135deg, #3567D6 0%, #170C6B 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 8px 24px rgba(53,103,214,0.35)",
            }}
          >
            <AutoAwesomeIcon sx={{ color: "#fff", fontSize: 30 }} />
          </Box>
        </Stack>

        {/* Text */}
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: "18px",
            color: "#170C6B",
            textAlign: "center",
            mb: 0.75,
          }}
        >
          Coming Soon! 🚀
        </Typography>
        <Typography
          sx={{
            fontSize: "13px",
            color: "#6B7280",
            textAlign: "center",
            lineHeight: 1.6,
          }}
        >
          Advanced AI Insights with demographic-based analytics are currently
          under development. We'll notify you as soon as they launch!
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3.5, pb: 3, justifyContent: "center", gap: 1.5 }}>
        <Button
          onClick={() => setComingSoonOpen(false)}
          sx={{
            borderRadius: "12px",
            border: "1.5px solid #D0DEFF",
            background: "#fff",
            color: "#3567D6",
            fontWeight: 600,
            px: 3,
            "&:hover": { background: "#f0f4ff", border: "1.5px solid #3567D6" },
          }}
        >
          Close
        </Button>
        <Button
          onClick={() => setComingSoonOpen(false)}
          sx={{
            borderRadius: "12px",
            background: "linear-gradient(135deg, #3567D6, #170C6B)",
            color: "#fff",
            fontWeight: 700,
            px: 3,
            boxShadow: "0 4px 16px rgba(53,103,214,0.35)",
            "&:hover": { opacity: 0.9 },
          }}
        >
          OK, Got It
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Stack
      direction={{ xs: "column", lg: "row" }}
      gap={2.5}
      alignItems="stretch"
      sx={{ 
        width: "100%",
      }}
    >
      {/* ══════════════════════════════════════════════
          LEFT COLUMN — AI Insights (2/3 width)
      ══════════════════════════════════════════════ */}
      <Box
        sx={{
          flex: "2 1 0",
          minWidth: 0,
          background: "#fff",
          borderRadius: "16px",
          p: { xs: "20px", md: "24px" },
          boxShadow: "0 2px 20px rgba(53,103,214,0.08)",
          border: "1px solid #EEF2FF",
          flexDirection: "column",
          height: "100%",
          display: "none",
        }}
      >
        {/* Header row */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={2.5}
        >
          <Stack direction="row" alignItems="center" gap={1.5}>
            {/* Icon badge */}
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "14px",
                background: "linear-gradient(135deg, #3567D6 0%, #170C6B 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 14px rgba(53,103,214,0.3)",
                flexShrink: 0,
              }}
            >
              <AutoAwesomeIcon sx={{ color: "#fff", fontSize: 22 }} />
            </Box>
            <Box>
              <Typography
                sx={{ fontWeight: 800, fontSize: "16px", color: "#170C6B", lineHeight: 1.2 }}
              >
                AI Insights & Recommendations
              </Typography>
              <Typography sx={{ fontSize: "11.5px", color: "#9DA3AE", mt: 0.25 }}>
                Smart alerts powered by machine learning
              </Typography>
            </Box>
          </Stack>

          {/* Free Plan badge */}
          <Box
            sx={{
              background: "#F0F6FF",
              border: "1.5px solid #C7D9FF",
              borderRadius: "10px",
              px: 1.4,
              py: 0.55,
              flexShrink: 0,
            }}
          >
            <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#3567D6" }}>
              Free Plan
            </Typography>
          </Box>
        </Stack>

        {/* Two insight cards */}
        <Stack direction={{ xs: "column", sm: "row" }} gap={1.75} mb={2}>
          {insightCards.map((card) => (
            <Box
              key={card.id}
              sx={{
                flex: 1,
                background: card.bg,
                borderRadius: "16px",
                border: `1.5px solid ${card.borderColor}`,
                p: "16px",
                transition: "transform 0.18s ease, box-shadow 0.18s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 24px rgba(53,103,214,0.12)",
                },
              }}
            >
              {/* Icon */}
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "10px",
                  background: card.iconBg,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 1.25,
                }}
              >
                {card.icon}
              </Box>
              <Typography sx={{ fontWeight: 800, fontSize: "13.5px", color: "#170C6B", mb: 0.6 }}>
                {card.title}
              </Typography>
              <Typography sx={{ fontSize: "12px", color: "#5D6A82", lineHeight: 1.6 }}>
                {card.body}
              </Typography>
            </Box>
          ))}
        </Stack>

        {/* Locked card */}
        <Box
          sx={{
            background: "#F8FAFF",
            borderRadius: "14px",
            border: "2px dashed #CBD5E1",
            p: "18px 20px",
            display: "flex",
            alignItems: "center",
            gap: 2,
            mt: "auto", // pushes down so layout stays crisp
          }}
        >
          {/* Lock icon */}
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "14px",
              background: "linear-gradient(135deg, #374151 0%, #1F2937 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
            }}
          >
            <LockOutlinedIcon sx={{ color: "#fff", fontSize: 22 }} />
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 800, fontSize: "14px", color: "#170C6B", mb: 0.5 }}>
              Unlock Advanced AI Insights
            </Typography>
            <Typography sx={{ fontSize: "12px", color: "#6B7280", lineHeight: 1.6, mb: 1.5 }}>
              Demographic-based insights by age and gender are available in the premium plan.
              Get deeper analytics and personalized recommendations.
            </Typography>
            <Button
              onClick={() => setComingSoonOpen(true)}
              startIcon={<LockOutlinedIcon sx={{ fontSize: "15px !important" }} />}
              sx={{
                borderRadius: "12px",
                background: "linear-gradient(135deg, #3567D6 0%, #06B6D4 100%)",
                color: "#fff",
                fontWeight: 700,
                fontSize: "13px",
                px: 2.5,
                py: 0.85,
                textTransform: "none",
                boxShadow: "0 4px 16px rgba(53,103,214,0.35)",
                "&:hover": {
                  background: "linear-gradient(135deg, #2a56c0 0%, #0497b0 100%)",
                  boxShadow: "0 6px 20px rgba(53,103,214,0.45)",
                },
                transition: "all 0.2s ease",
              }}
            >
              Upgrade to Unlock
            </Button>
          </Box>
        </Box>
      </Box>

      {/* ══════════════════════════════════════════════
          RIGHT COLUMN — Choose Your Plan (1/3 width)
      ══════════════════════════════════════════════ */}
      <Box
        sx={{
          flex: "1 1 0",
          width: "100%",
          height: "100%",
          "& > div": {
            height: "100%", // forces the PackageDetailSection container to stretch
            display: "flex",
            flexDirection: "column",
          }
        }}
      >
        <PackageDetailSection isScrollable={true} />
      </Box>

      {/* Coming Soon Dialog */}
      {comingSoonDialog()}
    </Stack>
  );
};

export default AiInsightsAndPlanSection;
